import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useEffect, useRef } from "react";
import { type NotificationDto, useNotificationApi } from "@/api/notifications";
import { getAccessTokenProvider } from "@/auth/access-token-provider";
import { GLOBAL_CONFIG } from "@/global-config";
import { getAcceptLanguage } from "@/locales/i18n";
import { useI18n } from "@/locales/i18n-provider";
import { type NotificationStreamCheckpoint, prepareNotificationStreamCheckpoint, subscribeToNotifications } from "./notification-stream";

type UseNotificationStreamOptions = {
  agentId: string;
  checkpoint: NotificationStreamCheckpoint;
  onNotificationReceived: (notification: NotificationDto) => void;
  onUnreadCountChanged: (count: number) => void;
};

function consoleHubUrl() {
  const url = new URL(GLOBAL_CONFIG.apiBaseUrl, window.location.origin);
  url.pathname = `${url.pathname.replace(/\/api\/admin\/?$/, "").replace(/\/$/, "")}/hub/console`;
  url.search = "";
  url.hash = "";
  return url.toString();
}

/** Keep the admitted agent's cursor across temporary unmounts during token renewal. */
export function useNotificationStream({ agentId, checkpoint, onNotificationReceived, onUnreadCountChanged }: UseNotificationStreamOptions): void {
  const api = useNotificationApi();
  const { locale } = useI18n();
  const latest = useRef({ api, locale, onNotificationReceived, onUnreadCountChanged });
  latest.current = { api, locale, onNotificationReceived, onUnreadCountChanged };

  useEffect(() => {
    if (!agentId) return;
    prepareNotificationStreamCheckpoint(checkpoint, agentId);
    const connection = new HubConnectionBuilder()
      .withUrl(consoleHubUrl(), {
        accessTokenFactory: async () => (await getAccessTokenProvider().getAccessToken()) ?? "",
        headers: { "Accept-Language": getAcceptLanguage(latest.current.locale) },
      })
      .withAutomaticReconnect([0, 2_000, 10_000, 30_000])
      .configureLogging(LogLevel.Warning)
      .build();

    return subscribeToNotifications({
      connection,
      checkpoint,
      api: {
        getList: (...args) => latest.current.api.getList(...args),
        getChanges: (...args) => latest.current.api.getChanges(...args),
        getUnreadCount: (...args) => latest.current.api.getUnreadCount(...args),
      },
      onNotificationReceived: (notification) => latest.current.onNotificationReceived(notification),
      onUnreadCountChanged: (count) => latest.current.onUnreadCountChanged(count),
    });
  }, [agentId, checkpoint]);
}
